import { apply, bump, celebrate, counter, doChore, give, has, onceToday, take, toast, burst, allChoresDone } from '../core/effects';
import { S } from '../core/game';
import { dayId, WATER_MAX, XiaoyuPhase } from '../core/state';
import { m } from '../i18n';
import { NOTE_FREQS, pianoNote, sfx } from '../audio/sfx';
import { say as speak, VOICES } from '../audio/speech';
import { clearNextExtra, say, setNextExtra, type Choice } from '../ui/dialog';
import { chatWith, hasChat } from './conversations';
import type { ObjectId } from '../world/map';
import type { Target } from '../world/world';
import { BEDTIME, completeObjective, todaysObjectives } from './days';
import { openEnglish, onEnglishClose } from '../ui/english';
import { openPuzzle } from '../ui/puzzle';
import { openRadio } from '../phone/radio';
import { endDay } from './dayCycle';
import { EXTRA_OBJECTS, questOptions, talkExtra, xiaoyuSaturday } from './extras';
import { giftChoice, PLACE_OBJECTS, talkPlaces } from './places';
import { openMinigame } from '../minigames/registry';
import { dig } from './treasure';
import { talkWorld3, WORLD3_OBJECTS } from './world3';
import { FEST_OBJECTS } from './festivals';
import { talkTrips, TRIP_OBJECTS } from './trips';
import { objectHandler, talkRegistered } from './registry';
import { practice } from './journal';
import { storyOptions } from './story';
import { impulseAd } from './lifeMoney';

const min = (n: number) => m().common.minutes(n);

const HOBBY_OF: Partial<Record<string, string>> = {
  piano: 'music', easel: 'art', libShelf: 'reading', boComics: 'reading', grPhotos: 'reading',
  pool: 'sport', gymNet: 'sport', tennisNet: 'sport', volleyNet: 'sport', field: 'sport',
  pond: 'nature', bush1: 'nature', bush2: 'nature', bush3: 'nature', kiteTree: 'nature', hlView: 'nature', hlSea: 'nature',
  boLego: 'craft', grSewing: 'craft', grCooker: 'craft',
};

/** Entry point from the world: the player interacts with something. */
export function interact(t: Target): void {
  if (t.kind === 'npc') return talk(t.npc.id);
  if (t.kind === 'spot') return dig(t.spot);
  const id = t.obj.id;
  // Hobbies grow with what you choose to do.
  const hobby = HOBBY_OF[id] ?? (id.startsWith('fam_plot') ? 'nature' : undefined);
  if (hobby) practice(hobby);
  if (id === 'tv' && Math.random() < 0.2) window.setTimeout(() => impulseAd('tv'), 1500);
  // Registered package handlers come first, so a package can extend an existing object (the oven…).
  (objectHandler(id) ?? OBJECT_HANDLERS[id] ?? EXTRA_OBJECTS[id] ?? PLACE_OBJECTS[id] ?? WORLD3_OBJECTS[id] ?? FEST_OBJECTS[id] ?? TRIP_OBJECTS[id])?.();
}

function already(): void {
  toast(m().common.alreadyToday);
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

const OBJECT_HANDLERS: Partial<Record<ObjectId, () => void>> = {
  desk() {
    const t = m().desk;
    say({
      who: m().objects.desk,
      icon: '🖥️',
      text: t.text,
      choices: [
        {
          text: t.log,
          cost: min(15),
          run: () => {
            if (!onceToday('log')) return already();
            apply({ study: 6, stars: 3, minutes: 15 });
            completeObjective('log');
            say({ who: m().objects.desk, icon: '🖥️', text: m().desk.logDone });
          },
        },
        { text: t.english, run: openEnglish },
        { text: t.schedule, run: showGoals },
      ],
    });
  },

  tea() {
    const t = m().tea;
    say({
      who: m().objects.tea,
      icon: '🍵',
      text: t.text,
      choices: [
        {
          text: t.drink,
          cost: min(5),
          run: () => {
            if (bump('cocoa') > 3) return already();
            sfx('brew');
            apply({ energy: 18, bladder: 6, minutes: 5 });
            toast(m().tea.drinkToast);
          },
        },
        {
          text: t.share,
          cost: min(5),
          run: () => {
            if (!onceToday('teaShare')) return already();
            sfx('brew');
            apply({ minutes: 5 });
            give('tea', 3);
            toast(m().tea.shareToast);
          },
        },
        {
          text: t.watch,
          cost: min(2),
          run: () => {
            sfx('water');
            apply({ mood: 6, minutes: 2 });
            toast(m().tea.watchToast);
          },
        },
      ],
    });
  },

  water() {
    const t = m().water;
    say({
      who: m().objects.water,
      icon: '🚰',
      text: t.text,
      choices: [
        { text: t.cold, run: () => (sfx('water'), burst('drop', 6), apply({ energy: 6, bladder: 10, minutes: 1 }), toast(m().water.coldToast)) },
        { text: t.hot, run: () => (sfx('water'), apply({ mood: 8, bladder: 8, minutes: 2 }), toast(m().water.hotToast)) },
        {
          text: `${t.refill} (${S().bag.water}/${WATER_MAX})`,
          run: () => {
            sfx('water');
            S().bag.water = WATER_MAX;
            apply({ minutes: 1 }, { float: false });
            toast(m().water.refillToast);
          },
        },
      ],
    });
  },

  shelfL: shelf,
  shelfR: shelf,

  plant() {
    const t = m().plant;
    say({
      who: m().objects.plant,
      icon: '🪴',
      text: t.text,
      choices: [
        { text: t.water, run: () => (onceToday('plantWater') ? (sfx('water'), burst('drop', 8), apply({ mood: 6, minutes: 2 }), toast(m().plant.waterToast)) : already()) },
        { text: t.prune, cost: min(10), run: () => (onceToday('plantPrune') ? (sfx('ok'), apply({ study: 3, mood: 4, minutes: 10 }), toast(m().plant.pruneToast)) : already()) },
      ],
    });
  },

  sofa() {
    const t = m().sofa;
    say({
      who: m().objects.sofa,
      icon: '🛋️',
      text: t.text,
      choices: [{ text: t.nap, cost: min(20), run: () => (sfx('ok'), apply({ energy: 15, mood: 10, minutes: 20 }), toast(m().sofa.napToast)) }],
    });
  },

  toilet: useToilet,

  easel() {
    const t = m().art;
    say({
      who: t.title,
      icon: '🎨',
      text: t.text,
      choices: [
        {
          text: t.quiz,
          run: () => {
            const q = m().art;
            say({
              who: q.title,
              icon: '🎨',
              text: q.quizQ,
              choices: q.quizA.map((a, i) => ({
                text: a,
                run: () => {
                  if (i === 0) {
                    toast(m().art.quizRight);
                    if (onceToday('artQuiz')) {
                      celebrate();
                      apply({ stars: 6, mood: 5 });
                    }
                  } else {
                    sfx('bad');
                    toast(m().art.quizWrong[i - 1]);
                  }
                },
              })),
            });
          },
        },
        { text: `🎨 ${m().arcade.games.drawing.name}`, run: () => openMinigame('drawing') },
        {
          text: t.steps,
          run: () =>
            say({
              who: m().art.title,
              icon: '🖼️',
              text: m().art.stepsText,
              choices: [{ text: m().art.stepsClaim, run: () => (onceToday('artSteps') ? (celebrate(), apply({ stars: 5 })) : already()) }],
            }),
        },
      ],
    });
  },

  chineseDesk() {
    const w = m().writing;
    const challenge = (key: 'a' | 'b') => {
      const t = m().writing;
      const good = key === 'a' ? t.aGood : t.bGood;
      const bad = key === 'a' ? t.aBad : t.bBad;
      const opts = [
        { text: good, ok: true },
        { text: bad, ok: false },
      ].sort(() => Math.random() - 0.5);
      say({
        who: t.title,
        icon: '✍️',
        text: key === 'a' ? t.aQ : t.bQ,
        choices: opts.map((o) => ({
          text: o.text,
          run: () => {
            if (!o.ok) {
              sfx('bad');
              return toast(key === 'a' ? m().writing.aWrong : m().writing.bWrong);
            }
            toast(key === 'a' ? m().writing.aRight : m().writing.bRight);
            if (onceToday(`writing.${key}`)) {
              celebrate();
              apply({ stars: 5, study: 5 });
            }
          },
        })),
      });
    };
    say({
      who: w.title,
      icon: '✍️',
      text: w.text,
      choices: [
        { text: w.a, run: () => challenge('a') },
        { text: w.b, run: () => challenge('b') },
      ],
    });
  },

  piano() {
    const t = m().piano;
    say({
      who: t.title,
      icon: '🎹',
      text: t.text,
      choices: [
        { text: t.free, run: freePiano },
        { text: t.ear, run: earTest },
      ],
    });
  },

  board() {
    say({ who: m().board.title, icon: '📋', text: `${m().board.text}\n\n${goalsText()}` });
  },

  benchL: bench,
  benchR: bench,

  picnic() {
    if (dayId(S()) === 'sat' && S().time >= 8 * 60 && S().time < 19 * 60) return talk('emma');
    say({ who: m().objects.picnic, icon: '🧺', text: m().emma.absent });
  },

  field() {
    const t = m().catchBall;
    const winds = ['left', 'right', 'none'] as const;
    const wind = winds[Math.floor(Math.random() * winds.length)];
    const answer = wind === 'none' ? 'stay' : wind;
    const pick = (choice: 'left' | 'right' | 'stay') => () => {
      apply({ energy: -5, minutes: 5 }, { float: false });
      if (choice === answer) {
        toast(m().catchBall.caught);
        if (onceToday('catch')) {
          celebrate();
          apply({ stars: 5, mood: 6 });
        } else sfx('ok');
      } else {
        sfx('bad');
        toast(m().catchBall.missed);
      }
    };
    say({
      who: t.title,
      icon: '⚾',
      text: t.text(t.winds[wind]),
      choices: [
        { text: `⚾ ${m().arcade.games.baseball.name}`, run: () => openMinigame('baseball') },
        { text: t.left, run: pick('left') },
        { text: t.right, run: pick('right') },
        { text: t.stay, run: pick('stay') },
      ],
    });
  },

  shoes() {
    const t = m().shoes;
    say({
      who: m().objects.shoes,
      icon: '👟',
      text: t.text,
      choices: [
        {
          text: t.act,
          cost: min(2),
          run: () => {
            if (!doChore('shoes')) return already();
            apply({ stars: 3, minutes: 2, love: { mom: 10 } });
            sfx('ok');
            burst('heart', 4);
            toast(m().shoes.done);
            choreCheck();
          },
        },
      ],
    });
  },

  sink() {
    const t = m().sink;
    say({
      who: m().objects.sink,
      icon: '🥣',
      text: t.text,
      choices: [
        {
          text: t.act,
          cost: min(15),
          run: () => {
            if (!doChore('dishes')) return already();
            sfx('water');
            burst('drop', 10);
            apply({ stars: 5, energy: -4, mood: 8, minutes: 15, love: { mom: 15 } });
            toast(m().sink.done);
            completeObjective('dishes');
            choreCheck();
          },
        },
      ],
    });
  },

  oven() {
    const t = m().oven;
    say({
      who: t.title,
      icon: '🍰',
      text: t.text,
      choices: [
        {
          text: t.cookies,
          cost: min(20),
          run: () => {
            sfx('brew');
            apply({ energy: -3, minutes: 20 }, { float: false });
            toast(m().oven.cookiesWork);
            window.setTimeout(() => {
              give('cookies', 3);
              apply({ mood: 8 });
              celebrate();
              toast(m().oven.cookiesDone);
              completeObjective('cookies');
            }, 900);
          },
        },
        {
          text: `${t.cake} (🍓)`,
          cost: min(40),
          run: () => {
            if (!take('strawberries')) {
              sfx('bad');
              return toast(m().oven.cakeNeeds);
            }
            sfx('brew');
            apply({ energy: -5, minutes: 40 }, { float: false });
            toast(m().oven.cakeWork);
            window.setTimeout(() => {
              give('cake', 1);
              apply({ mood: 12 });
              celebrate(true);
              toast(m().oven.cakeDone);
              completeObjective('cake');
            }, 1100);
          },
        },
      ],
    });
  },

  homeDesk() {
    const t = m().homeDesk;
    say({
      who: m().objects.homeDesk,
      icon: '✏️',
      text: t.text,
      choices: [
        {
          text: t.act,
          cost: min(45),
          run: () => {
            if (!doChore('homework')) return already();
            celebrate();
            apply({ study: 15, stars: 6, energy: -6, minutes: 45, love: { mom: 10 } });
            toast(m().homeDesk.done);
            completeObjective('homework');
            choreCheck();
            window.setTimeout(openEnglish, 600);
          },
        },
      ],
    });
  },

  bath() {
    const t = m().bath;
    say({
      who: m().objects.bath,
      icon: '🛁',
      text: t.text,
      choices: [
        {
          text: t.shower,
          cost: min(20),
          run: () => {
            if (!doChore('shower')) return already();
            sfx('water');
            burst('drop', 12);
            apply({ energy: 15, mood: 15, minutes: 20, love: { mom: 5 } });
            toast(m().bath.showerDone);
            choreCheck();
          },
        },
        { text: t.toilet, run: useToilet },
      ],
    });
  },

  bed() {
    const t = m().sleep;
    const s = S();
    const late = s.time >= BEDTIME;
    say({
      who: t.title,
      icon: '🛏️',
      text: late ? m().status.lateNight : t.early,
      choices: [
        late
          ? { text: t.sleep, run: () => endDay() }
          : { text: t.nap, cost: min(30), run: () => (sfx('ok'), apply({ energy: 20, mood: 4, minutes: 30 })) },
        { text: t.stay },
      ],
    });
  },
};

function shelf(): void {
  const t = m().shelf;
  say({
    who: m().objects.shelf,
    icon: '📚',
    text: t.text,
    choices: [
      {
        text: t.read,
        cost: min(15),
        run: () => {
          if (!onceToday('read')) return already();
          sfx('ok');
          apply({ study: 8, stars: 2, minutes: 15 });
          toast(m().shelf.readToast);
        },
      },
      {
        text: t.borrow,
        run: () => {
          if (!onceToday('borrow')) return already();
          give('book', 1);
          toast(m().shelf.borrowToast);
        },
      },
      { text: t.english, run: openEnglish },
    ],
  });
}

function bench(): void {
  const t = m().bench;
  say({
    who: m().objects.bench,
    icon: '🪑',
    text: t.text,
    choices: [{ text: t.rest, cost: min(10), run: () => (apply({ mood: 6, energy: 4, minutes: 10 }), toast(m().bench.restToast)) }],
  });
}

export function useToilet(): void {
  const s = S();
  if (s.stats.bladder < 12) return toast(m().toilet.notNeeded);
  sfx('flush');
  s.stats.bladder = 0;
  s.daily['warn.bladder'] = false;
  apply({ mood: 3, minutes: 3 });
  toast(m().toilet.done);
}

function freePiano(): void {
  const t = m().piano;
  const keys: Choice[] = Object.entries(NOTE_FREQS).map(([name, f]) => ({
    text: `♪ ${name}`,
    keepOpen: true,
    run: () => {
      pianoNote(f);
      burst('note', 1);
    },
  }));
  keys.push({
    text: t.finish,
    run: () => {
      if (!onceToday('pianoFree')) return already();
      celebrate();
      apply({ stars: 3, mood: 4 });
    },
  });
  say({ who: t.title, icon: '🎹', text: t.freeText, choices: keys });
}

function earTest(): void {
  const names = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const answer = names[Math.floor(Math.random() * names.length)];
  const play = () => pianoNote(NOTE_FREQS[answer]);
  play();
  const t = m().piano;
  say({
    who: t.title,
    icon: '👂',
    text: t.earQ,
    choices: [
      { text: t.replay, keepOpen: true, run: play },
      ...names.map((n) => ({
        text: t.note(n),
        run: () => {
          if (n === answer) {
            toast(m().piano.right);
            if (counter('earStars') < 3) {
              bump('earStars');
              celebrate();
              apply({ stars: 6 });
            } else sfx('ok');
          } else {
            sfx('bad');
            toast(m().piano.wrong(answer));
          }
        },
      })),
    ],
  });
}

function choreCheck(): void {
  if (!allChoresDone() || !onceToday('allChores')) return;
  window.setTimeout(() => {
    const t = m().chores;
    say({
      who: t.allTitle,
      icon: '🌟',
      text: t.allText,
      choices: [
        {
          text: t.allClaim,
          run: () => {
            celebrate(true);
            apply({ stars: 20, coins: 50, mood: 30, love: { mom: 20 } });
          },
        },
      ],
    });
  }, 700);
}

function goalsText(): string {
  return `**${m().objectives.title}**\n` + todaysObjectives().map((o) => `${o.done ? '✅' : '⬜'} ${o.text}`).join('\n');
}

export function showGoals(): void {
  say({ who: m().objectives.title, icon: '🚩', text: goalsText() });
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function talk(id: string): void {
  // Every conversation also offers a rotating small-talk topic.
  const extra = [];
  if (hasChat(id)) extra.push({ text: m().talks.chat, run: () => chatWith(id) });
  const gift = giftChoice(id);
  if (gift && id !== 'cat' && id !== 'horse' && id !== 'pony') extra.push(gift);
  // Story quests: offers, errands and hand-ins go first so they are never missed.
  setNextExtra([...storyOptions(id), ...extra]);
  try {
    talkInner(id);
  } finally {
    clearNextExtra();
  }
}

function talkInner(id: string): void {
  switch (id) {
    case 'mom':
      return talkMom();
    case 'sister':
      return talkSister();
    case 'dad':
      return talkDad();
    case 'xiaoyu':
      return talkXiaoyu();
    case 'teacher':
      return talkTeacher();
    case 'chunbo':
      return talkChunbo();
    case 'emma':
      return talkEmma();
    default:
      if (!talkRegistered(id) && !talkExtra(id) && !talkWorld3(id) && !talkTrips(id)) talkPlaces(id);
  }
}

function talkMom(): void {
  const t = m().mom;
  const who = `${m().npc.mom} ❤️`;
  const reply = (text: string) => {
    speak(text, VOICES.mom);
    say({ who, icon: '👩', text: `“${text}”` });
  };
  say({
    who,
    icon: '👩',
    text: t.lines[Math.floor(Math.random() * t.lines.length)],
    choices: [
      {
        text: `${t.giveCake} (🍰×${S().bag.cake})`,
        run: () => {
          if (!take('cake')) return toast(m().mom.giveCakeNone);
          celebrate(true);
          burst('heart', 10);
          apply({ mood: 15, love: { mom: 25 } });
          completeObjective('giveCake');
          reply(m().mom.giveCakeReply);
        },
      },
      {
        text: `${t.giveCookies} (🍪×${S().bag.cookies})`,
        run: () => {
          if (!take('cookies')) return toast(m().mom.giveCookiesNone);
          sfx('ok');
          burst('heart', 6);
          apply({ mood: 6, love: onceToday('momCookies') ? { mom: 15 } : {} });
          reply(m().mom.giveCookiesReply);
        },
      },
      ...questOptions.mom(),
      {
        text: t.hug,
        run: () => {
          if (!onceToday('hugMom')) return say({ who, icon: '👩', text: m().mom.tired });
          sfx('ok');
          burst('heart', 8);
          apply({ mood: 10, love: { mom: 10 } });
          reply(m().mom.hugReply);
        },
      },
    ],
  });
}

function talkSister(): void {
  const t = m().sister;
  say({
    who: m().npc.sister,
    icon: '👧',
    text: t.text,
    choices: [
      {
        text: `${t.read} (📖×${S().bag.book})`,
        cost: min(20),
        run: () => {
          if (!has('book')) return toast(m().sister.readNoBook);
          if (!doChore('sister')) return already();
          celebrate();
          burst('heart', 6);
          apply({ mood: 12, stars: 4, minutes: 20, love: { sister: 15, mom: 10 } });
          toast(m().sister.readDone);
          completeObjective('sister');
          choreCheck();
        },
      },
      { text: t.puzzle, run: openPuzzle },
    ],
  });
}

function talkDad(): void {
  const t = m().dad;
  say({
    who: m().npc.dad,
    icon: '👨',
    text: t.hello,
    choices: [
      {
        text: t.hug,
        run: () => {
          if (!onceToday('hugDad')) return already();
          burst('heart', 8);
          sfx('ok');
          apply({ mood: 8, love: { dad: 10 } });
          speak(m().dad.hugReply, VOICES.dad);
          say({ who: m().npc.dad, icon: '👨', text: `“${m().dad.hugReply}”` });
        },
      },
    ],
  });
}

function talkXiaoyu(): void {
  const s = S();
  if (dayId(s) === 'sat') return xiaoyuSaturday();
  const t = m().xiaoyu;
  if (s.xiaoyu.phase >= XiaoyuPhase.Helped) {
    say({ who: t.title, icon: '🧑', text: t.later });
    return;
  }
  const finish = () =>
    say({
      who: t.title,
      icon: '🧑',
      text: m().xiaoyu.better,
      choices: [
        {
          text: m().xiaoyu.promise,
          run: () => {
            S().xiaoyu.phase = XiaoyuPhase.Helped;
            S().forever.xiaoyuHelped = true;
            celebrate(true);
            apply({ stars: 10, love: { xiaoyu: 30 } });
            toast(m().xiaoyu.done);
            completeObjective('xiaoyu');
          },
        },
      ],
    });
  say({
    who: t.title,
    icon: '🧑',
    text: t.open,
    choices: [
      {
        text: t.sitDown,
        run: () => {
          apply({ mood: 4, love: { xiaoyu: 10 } }, { float: false });
          say({
            who: m().xiaoyu.title,
            icon: '🧑',
            text: m().xiaoyu.tired,
            choices: [
              { text: m().xiaoyu.safe, run: finish },
              {
                text: m().xiaoyu.english,
                run: () => {
                  finishLater = true;
                  openEnglish();
                },
              },
            ],
          });
        },
      },
      {
        text: `${t.candy} (🍬×${s.bag.candy})`,
        run: () => {
          if (!onceToday('xiaoyuCandy')) return already();
          if (!take('candy')) return toast(m().xiaoyu.candyNone);
          apply({ love: { xiaoyu: 12 } }, { float: false });
          toast(m().xiaoyu.candyDone);
        },
      },
    ],
  });
}

/** If the player chose English practice with Xiaoyu, the talk resolves when they come back. */
let finishLater = false;
onEnglishClose(resolveXiaoyuAfterEnglish);
function resolveXiaoyuAfterEnglish(): void {
  if (!finishLater) return;
  finishLater = false;
  const s = S();
  if (s.xiaoyu.phase === XiaoyuPhase.Seated) {
    s.xiaoyu.phase = XiaoyuPhase.Helped;
    s.forever.xiaoyuHelped = true;
    celebrate(true);
    apply({ stars: 10, love: { xiaoyu: 30 } });
    completeObjective('xiaoyu');
  }
}

function talkTeacher(): void {
  const t = m().teacher;
  const s = S();
  const who = m().npc.teacher;
  if (s.daily['obj.teacher']) return say({ who, icon: '👩‍🏫', text: t.after });
  if (s.xiaoyu.phase < XiaoyuPhase.Helped) return say({ who, icon: '👩‍🏫', text: t.before });
  say({
    who,
    icon: '👩‍🏫',
    text: t.before,
    choices: [
      {
        text: t.report,
        run: () => {
          celebrate();
          apply({ study: 10, stars: 4 });
          completeObjective('teacher');
          say({ who: m().npc.teacher, icon: '👩‍🏫', text: m().teacher.thanks });
        },
      },
    ],
  });
}

function talkChunbo(): void {
  const s = S();
  const t = m().chunbo;
  const n = s.npcTalk.chunbo ?? 0;
  s.npcTalk.chunbo = n + 1;
  const v = t.versions[n % t.versions.length];
  say({
    who: t.title,
    icon: '👮',
    text: v.intro,
    choices: [
      {
        text: v.give,
        run: () => {
          const c = m().chunbo;
          const first = onceToday('chunboGift');
          if (take('tea')) {
            toast(c.gotTea);
            apply({ love: { chunbo: first ? 15 : 3 }, stars: first ? 3 : 0 });
          } else if (take('candy')) {
            toast(c.gotCandy);
            apply({ love: { chunbo: first ? 10 : 2 }, stars: first ? 2 : 0 });
          } else toast(c.gotNothing);
          sfx('ok');
        },
      },
      ...questOptions.chunbo(),
      {
        text: v.learn,
        run: () => {
          const c = m().chunbo;
          say({
            who: c.tipTitle,
            icon: '👮',
            text: c.tip,
            choices: [{ text: c.remember, run: () => (onceToday('chunboTip') ? (celebrate(), apply({ stars: 3 })) : undefined) }],
          });
        },
      },
    ],
  });
}

function talkEmma(): void {
  const s = S();
  const t = m().emma;
  const picnic = dayId(s) === 'sat';
  if (picnic) {
    speak(t.picnicLine, VOICES.emma);
    say({
      who: `${m().npc.emma} 🧺`,
      icon: '👱‍♀️',
      text: t.picnicLine,
      choices: [
        {
          text: `${t.picnicShare} (🍪×${s.bag.cookies})`,
          run: () => {
            if (s.daily['obj.picnic']) return already();
            if (!take('cookies')) return toast(m().emma.picnicNone);
            celebrate(true);
            burst('heart', 6);
            apply({ mood: 25, stars: 8, minutes: 60, love: { emma: 20 } });
            completeObjective('picnic');
            say({ who: m().emma.picnicTitle, icon: '🧺', text: m().emma.picnicDone });
          },
        },
        ...questOptions.emma(),
        { text: t.english, run: openEnglish },
        { text: t.radio, run: openRadio },
      ],
    });
    return;
  }
  const n = s.npcTalk.emma ?? 0;
  s.npcTalk.emma = n + 1;
  const line = t.lines[n % t.lines.length];
  speak(line, VOICES.emma);
  say({
    who: m().npc.emma,
    icon: '👱‍♀️',
    text: line,
    choices: [
      ...questOptions.emma(),
      { text: t.english, run: openEnglish },
      { text: t.radio, run: openRadio },
      {
        text: `${t.cookies} (🍪×${s.bag.cookies})`,
        run: () => {
          if (!take('cookies')) return toast(m().emma.cookiesNone);
          sfx('ok');
          burst('heart', 5);
          apply({ love: { emma: onceToday('emmaCookies') ? 15 : 3 } }, { float: false });
          speak(m().emma.cookiesReply, VOICES.emma);
          say({ who: m().npc.emma, icon: '👱‍♀️', text: m().emma.cookiesReply });
        },
      },
    ],
  });
}
